import { useEffect } from "react";

export default function AboutPage() {
  useEffect(() => {
    document.title = "About — ISPYFR";
  }, []);

  return (
    <div className="prose prose-sm max-w-none text-text">
      <h1 className="font-display text-2xl font-bold">About ISPYFR</h1>

      <h2 className="mt-6 text-lg font-semibold">What Is ISPYFR?</h2>
      <p>
        ISPYFR — short for &ldquo;I Spy For Real&rdquo; — is a real-world scavenger
        hunt game you play with friends using your phone. One person creates a game
        by picking a list of items to find, shares an invite code with friends, and
        everyone races to photograph each item in the real world. A moderator
        reviews submissions to keep things fair, and at the end of the game, the
        player who found the most items wins.
      </p>
      <p>
        Think of it as a digital twist on the classic scavenger hunt. Instead of
        checking items off a paper list, you snap photos and submit them through
        the app. Instead of gathering everyone together to compare notes at the
        end, you can see results and standings in real time. ISPYFR brings the
        excitement of a physical scavenger hunt into the modern age while keeping
        everything simple, fun, and accessible.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Why We Built It</h2>
      <p>
        We believe the best games are the ones that get people moving, exploring,
        and spending time together in the real world. Scavenger hunts have been a
        staple of birthday parties, team-building events, and family gatherings for
        generations — but organizing one has always required a lot of manual work.
        Someone has to write out the item lists, print copies, explain the rules,
        collect results, and tally scores by hand.
      </p>
      <p>
        ISPYFR removes that friction. Creating a game takes under a minute. Inviting
        friends is as simple as sharing a four-character code. And the app handles
        submissions, moderation, and scoring automatically. We wanted to make it so
        easy to run a scavenger hunt that you could do it on a whim — at a park
        with your kids, during a lunch break with coworkers, or at a weekend
        get-together with friends.
      </p>
      <p>
        We also wanted to build something that respects your privacy and does not
        try to become yet another social media platform. There are no profiles to
        maintain, no follower counts, no algorithms deciding what you see. Just
        you, your friends, and a list of things to find.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Privacy by Design</h2>
      <p>
        Privacy is not an afterthought for ISPYFR — it is a core design principle.
        Every game is completely private and invitation-only. There is no public
        feed, no way to browse other people&apos;s games, and no discovery features
        that could expose your content to strangers.
      </p>
      <p>
        All game data is ephemeral. Photos, display names, game state, and
        submissions are automatically deleted after a short retention window
        following the end of each game. We do not keep anything long-term.
        There are no user profiles to scrape, no photo libraries to mine, and
        no behavioral data to sell. When your game is over and the retention
        window closes, your data is gone — permanently.
      </p>

      <h2 className="mt-6 text-lg font-semibold">No Accounts Required</h2>
      <p>
        One of the most deliberate decisions we made was to not require user
        accounts. There is no sign-up form, no password to remember, and no
        email address to hand over. When you join a game, you simply enter the
        invite code and pick a display name. That name exists only for the
        duration of that game — it is not tied to a persistent identity.
      </p>
      <p>
        This makes ISPYFR incredibly easy to use. Anyone can jump into a game in
        seconds, regardless of whether they have used the app before. It also
        means we collect virtually no personal information, which dramatically
        reduces privacy risk for everyone involved.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Built by South Fork Games</h2>
      <p>
        ISPYFR is built and operated by South Fork Games, a division of South Fork
        Mobile LLC based in Idaho, United States. We are a small team that
        believes in building software that is simple, respectful, and fun.
        ISPYFR is our first game title, and we are committed to keeping it a
        great experience for players of all ages.
      </p>
      <p>
        We chose to build ISPYFR as a web application so it works on any device
        with a browser — no app store downloads required. Whether you are on an
        iPhone, Android phone, tablet, or laptop, you can create or join a game
        instantly. The entire experience is optimized for mobile since that is
        where most players will be snapping photos, but it works great on larger
        screens too.
      </p>

      <h2 className="mt-6 text-lg font-semibold">What&apos;s Next</h2>
      <p>
        We have exciting plans for ISPYFR&apos;s future. We are exploring additional
        game modes beyond scavenger hunts, enhanced features for game creators,
        and quality-of-life improvements based on player feedback. Our goal is to
        make ISPYFR the go-to platform for real-world social games — the kind of
        games that get people off the couch, out the door, and having fun together.
      </p>
      <p>
        Have ideas or feedback? We would love to hear from you at{" "}
        <a
          href="mailto:support@ispyfr.com"
          className="text-primary hover:underline"
        >
          support@ispyfr.com
        </a>
        .
      </p>
    </div>
  );
}
